/**
 * Signed URL Manager
 *
 * Handles JWT token expiration issues with Supabase signed URLs
 *
 * Primary responsibilities:
 * - Create signed URLs with proper token management
 * - Refresh URLs when JWT tokens expire
 * - Cache URLs with expiration tracking
 * - Handle errors gracefully with fallback strategies
 */

import { Database } from '@/supabase/types'
import { getDesktopAccessTokenSupabaseClient } from '@/lib/desktop-supabase-client'
import { createBrowserSupabaseClient } from '@/lib/supabase/browser-client-options'
import {
  isModelReadableImageUrl,
  normalizeMessageImageStorageSource,
  rewriteSupabaseStorageUrl
} from '@/lib/media/safe-url'

interface CachedUrl {
  url: string
  expiresAt: number
  filePath: string
  bucket: string
}

class SignedUrlManager {
  private cache = new Map<string, CachedUrl>()
  private refreshPromises = new Map<string, Promise<string>>()
  private supabase = createBrowserSupabaseClient()

  /**
   * Get a signed URL with automatic refresh on token expiration
   */
  async getSignedUrl(
    filePath: string,
    bucket: string,
    expiresIn: number = 60 * 60 * 24 // 24 hours default
  ): Promise<string> {
    const cacheKey = `${bucket}:${filePath}`
    const now = Date.now()

    // Check if we have a valid cached URL
    const cached = this.cache.get(cacheKey)
    if (cached && cached.expiresAt > now + 60000) {
      // 1 minute buffer
      return cached.url
    }

    // Check if we're already refreshing this URL
    if (this.refreshPromises.has(cacheKey)) {
      return this.refreshPromises.get(cacheKey)!
    }

    // Create new signed URL
    const refreshPromise = this.createSignedUrl(filePath, bucket, expiresIn)
    this.refreshPromises.set(cacheKey, refreshPromise)

    try {
      const url = await refreshPromise
      this.cache.set(cacheKey, {
        url,
        expiresAt: now + expiresIn * 1000,
        filePath,
        bucket
      })
      return url
    } finally {
      this.refreshPromises.delete(cacheKey)
    }
  }

  /**
   * Create a signed URL with retry logic for token expiration
   */
  private async createSignedUrl(
    filePath: string,
    bucket: string,
    expiresIn: number
  ): Promise<string> {
    const desktopClient = getDesktopAccessTokenSupabaseClient()
    if (desktopClient) {
      const { data, error } = await desktopClient.storage
        .from(bucket)
        .createSignedUrl(filePath, expiresIn)

      if (error) {
        throw new Error(`Storage error: ${error.message}`)
      }

      if (!data?.signedUrl) {
        throw new Error('No signed URL returned')
      }

      return rewriteSupabaseStorageUrl(data.signedUrl)
    }

    const maxRetries = 3
    let lastError: Error | null = null

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        // Refresh the session before creating signed URL
        const {
          data: { session },
          error: sessionError
        } = await this.supabase.auth.getSession()

        if (sessionError) {
          throw new Error(`Session error: ${sessionError.message}`)
        }

        if (!session) {
          throw new Error('No active session')
        }

        // Create signed URL with current session
        const { data, error } = await this.supabase.storage
          .from(bucket)
          .createSignedUrl(filePath, expiresIn)

        if (error) {
          // If it's a JWT expiration error, try to refresh the session
          if (error.message.includes('JWT') || error.message.includes('expired')) {
            if (attempt < maxRetries) {
              // Refresh the session and retry
              const { error: refreshError } = await this.supabase.auth.refreshSession()
              if (refreshError) {
                lastError = new Error(`Token refresh failed: ${refreshError.message}`)
                continue
              }
              continue
            }
          }
          throw new Error(`Storage error: ${error.message}`)
        }

        if (!data?.signedUrl) {
          throw new Error('No signed URL returned')
        }

        return rewriteSupabaseStorageUrl(data.signedUrl)
      } catch (error) {
        lastError = error as Error

        // If it's a JWT error and we have retries left, wait and retry
        if (
          error instanceof Error &&
          (error.message.includes('JWT') || error.message.includes('expired')) &&
          attempt < maxRetries
        ) {
          // Wait before retry (exponential backoff)
          await new Promise((resolve) => setTimeout(resolve, Math.pow(2, attempt) * 1000))
          continue
        }

        // If it's not a JWT error or we're out of retries, throw
        throw error
      }
    }

    throw lastError || new Error('Failed to create signed URL after retries')
  }

  /**
   * Clear cache for a specific file or all files
   */
  clearCache(filePath?: string, bucket?: string) {
    if (filePath && bucket) {
      const cacheKey = `${bucket}:${filePath}`
      this.cache.delete(cacheKey)
    } else {
      this.cache.clear()
    }
  }

  /**
   * Get cache statistics for debugging
   */
  getCacheStats() {
    const now = Date.now()
    const valid = Array.from(this.cache.values()).filter((item) => item.expiresAt > now).length
    const expired = Array.from(this.cache.values()).filter((item) => item.expiresAt <= now).length

    return {
      total: this.cache.size,
      valid,
      expired,
      refreshing: this.refreshPromises.size
    }
  }
}

// Export singleton instance
export const signedUrlManager = new SignedUrlManager()

// Export individual functions for backward compatibility
export const getMessageImageFromStorage = async (filePath: unknown) => {
  const normalizedPath = normalizeMessageImageStorageSource(filePath)

  if (
    !normalizedPath ||
    isModelReadableImageUrl(normalizedPath) ||
    normalizedPath.startsWith('blob:') ||
    normalizedPath.startsWith('/api/image/serve?') ||
    normalizedPath.startsWith('api/image/serve?')
  ) {
    return normalizedPath || ''
  }

  return signedUrlManager.getSignedUrl(normalizedPath, 'message_images')
}

export const getWorkspaceImageFromStorage = async (filePath: string) => {
  return signedUrlManager.getSignedUrl(filePath, 'workspace_images')
}

/** Generated images bucket is public; return public URL (no signed URL needed). */
export const getGeneratedImageFromStorage = async (filePath: string) => {
  if (filePath.startsWith('http') || filePath.startsWith('data')) {
    return filePath
  }
  const client = createBrowserSupabaseClient()
  const { data } = client.storage.from('generated_images').getPublicUrl(filePath)
  return data.publicUrl
}

/** Generated videos bucket is public; return public URL (no signed URL needed). */
export const getGeneratedVideoFromStorage = async (filePath: string) => {
  if (filePath.startsWith('http') || filePath.startsWith('data')) {
    return filePath
  }
  const client = createBrowserSupabaseClient()
  const { data } = client.storage.from('generated_videos').getPublicUrl(filePath)
  return data.publicUrl
}

export const getAssistantImageFromStorage = async (filePath: string) => {
  return signedUrlManager.getSignedUrl(filePath, 'assistant_images')
}
